import { trackError, ErrorSeverity } from '@/utils/error-tracking';

const objectStoreName = "postImages";
const dbName = "imageDatabase";

// Typed window extension to avoid `any` casts
interface IDBWindow extends Window {
  idb?: IDBDatabase;
}

function getIdbWindow(): IDBWindow {
  return window as IDBWindow;
}

/**
 * Set once this page has learned it can never get an IndexedDB handle: the
 * global is missing, or `open()` threw synchronously. Every helper below
 * consults it before scheduling a retry, so a runtime without IndexedDB
 * degrades to in-memory photos instead of throwing out of the /sell mount
 * effect (or retrying `open()` every second for the life of the tab).
 */
let indexedDBUnavailable = false;
let unavailabilityReported = false;

/** Test seam: module state survives between Jest cases. */
export function __resetIndexedDBAvailabilityForTests(): void {
  indexedDBUnavailable = false;
  unavailabilityReported = false;
  reopenCount = 0;
}

/**
 * Whether this runtime exposes a usable IndexedDB. Some embedded WebViews
 * never define the global at all — the Google Search App on iOS 26 filed
 * `Can't find variable: indexedDB` from /sell on 2026-09-02 — and older
 * private-browsing modes throw from `open()`. Neither is an application
 * failure; the sell flow simply loses draft-photo persistence across reloads.
 */
export function isIndexedDBAvailable(): boolean {
  if (indexedDBUnavailable) return false;
  if (typeof indexedDB === 'undefined' || indexedDB === null) {
    markUnavailable('indexedDB global is not defined');
    return false;
  }
  return true;
}

function markUnavailable(reason: string, cause?: unknown, details?: Record<string, unknown>): void {
  indexedDBUnavailable = true;
  if (unavailabilityReported) return;
  unavailabilityReported = true;
  const error = cause instanceof Error ? cause : new Error(`IndexedDB unavailable: ${reason}`);
  // Warning, not error: the page keeps working, this is the record of how
  // often visitors arrive in a runtime without storage for draft photos.
  trackError(error, ErrorSeverity.WARNING, {
    action: 'initializeIndexDB',
    component: 'indexdb',
    extra: { reason, ...details },
  });
}

const probeDbName = `${dbName}-probe`;
const PROBE_TIMEOUT_MS = 3000;

function describeDomError(error: unknown): string {
  const named = error as { name?: unknown; message?: unknown } | null;
  return `${String(named?.name ?? 'no error')}: ${String(named?.message ?? '')}`;
}

/**
 * Whether a brand-new database opens where ours would not. Yes means our
 * database itself is broken; no means the browser's storage is.
 */
function probeFreshDatabase(): Promise<string> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve('no answer'), PROBE_TIMEOUT_MS);
    const finish = (outcome: string) => {
      clearTimeout(timer);
      resolve(outcome);
    };
    try {
      const probe = indexedDB.open(probeDbName, 1);
      probe.onsuccess = () => {
        try {
          probe.result.close();
          indexedDB.deleteDatabase(probeDbName);
        } catch {
          // The probe answered; leaving its empty database behind is harmless.
        }
        finish('opened');
      };
      probe.onerror = () => finish(`failed: ${describeDomError(probe.error)}`);
    } catch (error) {
      finish(`threw: ${describeDomError(error)}`);
    }
  });
}

/**
 * Chrome raises the same UnknownError "Internal error." from open() for a full
 * disk, blocked storage and a corrupt database, so naming the error (2026-09-29)
 * still could not tell them apart: two rows on 2026-10-28 from one Android
 * phone on /sell said only that. The storage estimate shows a full disk, and
 * the probe shows whether the fault is our database or all of storage.
 */
async function describeStorage(): Promise<Record<string, unknown>> {
  const details: Record<string, unknown> = {};
  try {
    const estimate = await navigator.storage?.estimate?.();
    if (estimate) {
      details.usageBytes = estimate.usage;
      details.quotaBytes = estimate.quota;
    } else {
      details.storageEstimate = 'unsupported';
    }
  } catch (error) {
    details.storageEstimate = `failed: ${describeDomError(error)}`;
  }
  details.freshDatabase = await probeFreshDatabase();
  return details;
}

async function reportOpenFailure(reason: string, cause: Error): Promise<void> {
  const details = await describeStorage();
  markUnavailable(reason, cause, details);
}

/**
 * Returns the open database, or undefined after arranging for `retry` to run
 * once the database has had a second to open. Returns undefined with no retry
 * when IndexedDB cannot be opened in this runtime at all.
 */
function acquireDb(retry: () => void): IDBDatabase | undefined {
  const db = getIdbWindow().idb;
  if (db) return db;
  if (!isIndexedDBAvailable()) return undefined;
  initializeIndexDB();
  if (indexedDBUnavailable) return undefined;
  setTimeout(retry, 1000);
  return undefined;
}

/**
 * The browser can close a connection the page still holds: clearing site
 * data, storage eviction, a failed backing store. window.idb then hands out a
 * dead handle for the life of the tab and every transaction() throws
 * InvalidStateError "The database connection is closing." On 2026-10-19 one
 * seller filed it 14 times from /sell, and the throw out of resetTable() after
 * a successful publish turned the success popup into "Failed to save listing".
 * So a closed handle is dropped and reopened, a few times at most per page.
 */
const MAX_REOPENS = 3;
let reopenCount = 0;

function forgetDb(db: IDBDatabase): void {
  const idbWindow = getIdbWindow();
  if (idbWindow.idb === db) idbWindow.idb = undefined;
}

function isClosedConnectionError(error: unknown): boolean {
  return (error as { name?: unknown } | null)?.name === 'InvalidStateError';
}

/**
 * Begin a transaction on `db`, or, when the browser has closed it, drop the
 * handle, reopen, and run `retry` once the new one is ready.
 */
function beginTransaction(db: IDBDatabase, mode: IDBTransactionMode, retry: () => void): IDBTransaction | undefined {
  try {
    return db.transaction(objectStoreName, mode);
  } catch (error) {
    if (!isClosedConnectionError(error)) throw error;
    forgetDb(db);
    reopenCount += 1;
    if (reopenCount > MAX_REOPENS) {
      markUnavailable('connection kept closing after reopen', error);
      return undefined;
    }
    acquireDb(retry);
    return undefined;
  }
}

export const initializeIndexDB = (): void => {
  if (!isIndexedDBAvailable()) return;

  let openRequest: IDBOpenDBRequest;
  try {
    openRequest = indexedDB.open(dbName, 1);
  } catch (error) {
    // Synchronous throw from open() (a SecurityError in restricted contexts)
    // means no later attempt will fare better; stop retrying for this page.
    markUnavailable('indexedDB.open threw', error);
    return;
  }

  // Set when onupgradeneeded gives up on the upgrade itself. The AbortError
  // open() reports next is then ours, and already filed as the reason.
  let upgradeAbandoned = false;

  openRequest.onupgradeneeded = function (event: IDBVersionChangeEvent) {
    const db = (event.target as IDBOpenDBRequest | null)?.result;
    if (!db) {
      // A Safari session on 2026-09-11 got an upgradeneeded with no result.
      // Reading objectStoreNames off it threw out of this handler, which
      // aborted the upgrade and filed an AbortError 8ms later. Abort it here
      // instead: letting version 1 commit without the store would leave a
      // database every later visit opens with no upgrade, then fails to read.
      upgradeAbandoned = true;
      markUnavailable('upgradeneeded fired without a database');
      try {
        openRequest.transaction?.abort();
      } catch {
        // A runtime that had no database for us may have no live upgrade either.
      }
      return;
    }
    if (!db.objectStoreNames.contains(objectStoreName)) {
      db.createObjectStore(objectStoreName);
    }
  };

  openRequest.onsuccess = function (event: Event) {
    // acquireDb hands out window.idb before it asks whether IndexedDB is
    // usable, so a database without the store must never be cached there.
    if (upgradeAbandoned) return;
    const db = (event.target as IDBOpenDBRequest).result;
    // Chrome fires close when it shuts the connection itself; forget it then,
    // rather than waiting for the next transaction() to throw.
    db.onclose = () => forgetDb(db);
    db.onversionchange = () => {
      db.close();
      forgetDb(db);
    };
    getIdbWindow().idb = db;
  };

  openRequest.onerror = function (event: Event) {
    if (upgradeAbandoned) return;
    // "Internal error." (UnknownError) is what blocked storage, quota and a
    // corrupt store all raise, and every retry fails the same way. Stop
    // retrying (acquireDb re-opened every second) and file one warning that
    // names the DOMException and what the storage looked like when it failed.
    // The retry latch is set now; the warning waits for the storage readings.
    indexedDBUnavailable = true;
    const openError = (event.target as IDBRequest).error;
    void reportOpenFailure(
      `indexedDB.open failed: ${openError?.name ?? 'no error'}: ${openError?.message ?? ''}`,
      openError ?? new Error("Error opening IndexedDB"),
    );
  };
};

/**
 * Draft photos are addressed by their IndexedDB key, never by a position.
 *
 * The page holds its photos as blob URLs in an array it reorders, edits and
 * trims in memory. Deleting "the photo at index 2" from the store deleted the
 * third key in storage order, which drifted from screen order the moment a
 * photo was dragged: after a reorder then a delete, the deleted photo came
 * back on reload and a different one was gone. So each URL the page shows is
 * mapped to the key its bytes live under, and every write goes through that.
 */
const keyByUrl = new Map<string, string>();

/** localStorage key holding the draft's photo keys in screen order. */
export const PHOTO_ORDER_STORAGE_KEY = "postImageOrder";

let keySequence = 0;

/** Keys sort in the order photos were added: time, then a tiebreak within a millisecond. */
function newImageKey(): string {
  keySequence = (keySequence + 1) % 10000;
  return `${Date.now()}_${String(keySequence).padStart(4, "0")}`;
}

/** Test seam: the URL-to-key map is module state and survives between Jest cases. */
export function __resetDraftPhotoKeysForTests(): void {
  keyByUrl.clear();
  keySequence = 0;
}

/** Test seam: map a shown URL to a stored key without writing any bytes. */
export function __setDraftPhotoKeyForTests(url: string, key: string): void {
  keyByUrl.set(url, key);
}

function readSavedOrder(): string[] {
  try {
    const raw = window.localStorage.getItem(PHOTO_ORDER_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === "string") : [];
  } catch (error) {
    // Blocked storage or a mangled value: the draft reloads in the order added.
    trackError(error instanceof Error ? error : new Error(String(error)), ErrorSeverity.WARNING, { action: 'readImageOrder', component: 'indexdb' });
    return [];
  }
}

function writeSavedOrder(keys: string[] | null): void {
  try {
    if (keys === null) window.localStorage.removeItem(PHOTO_ORDER_STORAGE_KEY);
    else window.localStorage.setItem(PHOTO_ORDER_STORAGE_KEY, JSON.stringify(keys));
  } catch (error) {
    // Blocked storage: the draft reloads in the order photos were added.
    trackError(error instanceof Error ? error : new Error(String(error)), ErrorSeverity.WARNING, { action: 'writeImageOrder', component: 'indexdb' });
  }
}

async function readImageBytes(image: Blob | string): Promise<ArrayBuffer> {
  if (typeof image === "string") {
    const response = await fetch(image);
    return response.arrayBuffer();
  }
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event: ProgressEvent<FileReader>) => resolve(event.target?.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error("Error reading image"));
    reader.readAsArrayBuffer(image);
  });
}

/**
 * Write `image` under `key`, unless `url` has since been deleted or replaced.
 * The check is what stops a slow first write from resurrecting a photo the
 * seller deleted, or overwriting the edit that replaced it.
 */
function putImage(key: string, url: string, image: Blob | string, action: string): void {
  const db = acquireDb(() => putImage(key, url, image, action));
  if (!db) return;
  void writeImageBytes(db, key, url, image, action);
}

async function writeImageBytes(db: IDBDatabase, key: string, url: string, image: Blob | string, action: string): Promise<void> {
  try {
    const bytes = await readImageBytes(image);
    if (keyByUrl.get(url) !== key) return;
    const transaction = beginTransaction(db, "readwrite", () => putImage(key, url, image, action));
    if (!transaction) return;
    const putRequest = transaction.objectStore(objectStoreName).put(bytes, key);
    putRequest.onerror = function (putEvent: Event) {
      trackError((putEvent.target as IDBRequest).error ?? new Error("Error storing image"), undefined, { action, component: 'indexdb' });
    };
  } catch (error) {
    trackError(error instanceof Error ? error : new Error(String(error)), undefined, { action, component: 'indexdb' });
  }
}

/**
 * Save a draft photo the page now shows at `url`. Store the bytes the page
 * shows (the resized image), so a reload brings back the same photo. Returns
 * the key it is saved under.
 */
export function storeImage(image: Blob, url: string): string {
  const key = newImageKey();
  keyByUrl.set(url, key);
  putImage(key, url, image, 'storeImage');
  return key;
}

/**
 * An edited or rotated photo keeps its slot: the new bytes go under the old
 * photo's key, and the new URL takes over that key. `image` defaults to the
 * bytes behind `newUrl`.
 */
export function replaceImage(oldUrl: string, newUrl: string, image?: Blob): void {
  const key = keyByUrl.get(oldUrl);
  if (key === undefined) return;
  keyByUrl.delete(oldUrl);
  keyByUrl.set(newUrl, key);
  putImage(key, newUrl, image ?? newUrl, 'replaceImage');
}

/** Remember the draft's photo order, so a reload shows them as the seller left them. */
export function saveImageOrder(urls: string[]): void {
  const keys = urls.map((url) => keyByUrl.get(url)).filter((k): k is string => k !== undefined);
  writeSavedOrder(keys);
}

/**
 * Hand every saved draft photo to the page, in the order the seller left them.
 * Keys missing from the saved order (added after the last reorder) follow in
 * the order they were added.
 */
export function fetchImages(saveImageToAppState: (imgUrl: string, key: string) => void): void {
  const db = acquireDb(() => fetchImages(saveImageToAppState));
  if (!db) return;

  const transaction = beginTransaction(db, "readonly", () => fetchImages(saveImageToAppState));
  if (!transaction) return;
  const objectStore = transaction.objectStore(objectStoreName);
  const getRequest = objectStore.getAllKeys();

  getRequest.onsuccess = function (event: Event) {
    const storedKeys = (event.target as IDBRequest).result as IDBValidKey[];
    const position = new Map(readSavedOrder().map((k, i) => [k, i] as const));
    const rank = (key: IDBValidKey, i: number) => position.get(String(key)) ?? position.size + i;
    const keys = storedKeys
      .map((key, i) => ({ key, r: rank(key, i) }))
      .sort((a, b) => a.r - b.r)
      .map(({ key }) => key);

    // Requests in one transaction complete in the order they were made, so
    // the page receives the photos in this order.
    for (const key of keys) {
      const imgRequest = objectStore.get(key);

      imgRequest.onsuccess = function (imgEvent: Event) {
        const imgArrayBuffer = (imgEvent.target as IDBRequest).result as ArrayBuffer;
        const blob = new Blob([imgArrayBuffer], { type: "image/jpeg" }); // Set the image type as needed
        const imgUrl = URL.createObjectURL(blob);
        keyByUrl.set(imgUrl, String(key));
        saveImageToAppState(imgUrl, key as string);
      };

      imgRequest.onerror = function (imgEvent: Event) {
        trackError((imgEvent.target as IDBRequest).error ?? new Error("Error fetching image"), undefined, { action: 'fetchImages', component: 'indexdb' });
      };
    }
  };

  getRequest.onerror = function (event: Event) {
    trackError((event.target as IDBRequest).error ?? new Error("Error fetching image keys"), undefined, { action: 'fetchImages', component: 'indexdb' });
  };
}

/** Delete the saved draft photo the page showed at `url`. */
export function deleteImage(url: string): void {
  const key = keyByUrl.get(url);
  if (key === undefined) return;
  keyByUrl.delete(url);
  deleteImageKey(key);
}

function deleteImageKey(key: string): void {
  const db = acquireDb(() => deleteImageKey(key));
  if (!db) return;

  try {
    const rwTransaction = beginTransaction(db, "readwrite", () => deleteImageKey(key));
    if (!rwTransaction) return;
    const deleteRequest = rwTransaction.objectStore(objectStoreName).delete(key);
    deleteRequest.onerror = function (deleteEvent: Event) {
      trackError((deleteEvent.target as IDBRequest).error ?? new Error("Error deleting image"), undefined, { action: 'deleteImage', component: 'indexdb' });
    };
  } catch (error) {
    trackError(error instanceof Error ? error : new Error(String(error)), undefined, { action: 'deleteImage', component: 'indexdb' });
  }
}

export function resetTable(): void {
  keyByUrl.clear();
  writeSavedOrder(null);
  clearStoredImages();
}

function clearStoredImages(): void {
  const db = acquireDb(() => clearStoredImages());
  if (!db) return;

  // Called from the publish success path: a throw here once reported a live
  // listing as a failed save, so nothing below may escape.
  const transaction = beginTransaction(db, "readonly", () => clearStoredImages());
  if (!transaction) return;
  const objectStore = transaction.objectStore(objectStoreName);
  const getRequest = objectStore.getAllKeys();

  getRequest.onsuccess = function (event: Event) {
    const keys = (event.target as IDBRequest).result as IDBValidKey[];
    if (keys.length === 0) return;
    const rwTransaction = beginTransaction(db, "readwrite", () => clearStoredImages());
    if (!rwTransaction) return;
    const rwObjectStore = rwTransaction.objectStore(objectStoreName);
    for (const key of keys) {
      try {
        const deleteRequest = rwObjectStore.delete(key);
        deleteRequest.onsuccess = function () {
          // Image deleted successfully
        };

        deleteRequest.onerror = function (deleteEvent: Event) {
          trackError((deleteEvent.target as IDBRequest).error ?? new Error("Error deleting image"), undefined, { action: 'resetTable', component: 'indexdb' });
        };
      } catch (_e) { /* ignore */ }
    }
  };
}
